import { Info } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekCheckbox, PeekStack, PeekTable, PeekTableBody, PeekTableCell, PeekTableContainer, PeekTableHeadFilter, PeekTableSkeletonLoader, PeekTableSortLabel, PeekTooltip, PeekTypography, } from "@piiqtechnologies/ui-components";
import { useRefetch } from "@piiqtechnologies/ui-lib";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import NoDataFound from "../no-data-found";
import { ArrowDrop } from "./ArrowDrop";
import TablePaginationContainer from "./TablePaginationContainer";
import { TableRow } from "./TableRow";
const DEFAULT_ROW_TRANSFORM = (row) => row;
function SelectableTableHead(props) {
    const { onSelectAllClick, numSelected, rowCount } = props;
    return (React.createElement(PeekTableCell, { padding: "checkbox" },
        React.createElement(PeekCheckbox, { color: "secondary", indeterminate: numSelected > 0 && numSelected < rowCount, checked: rowCount > 0 && numSelected === rowCount, onChange: onSelectAllClick, inputProps: {
                "aria-label": "Select all",
            }, sx: { paddingLeft: "4px !important" } })));
}
export function Table({ columns: _columns, api, notFoundMessage = "Data not found", apiConfig = {}, rowTransform = DEFAULT_ROW_TRANSFORM, actions, actionsCover, sortPredicate, tableProps, onClick, onOrderChange, onFiltersChange, onPageChange, onSelectionChange, canSelectRow, rowSelectedContent, rowExpandedContent, expandButton, }) {
    const intl = useIntl();
    const { refetch } = useRefetch();
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState();
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({});
    const [selectedRows, setSelectedRows] = React.useState([]);
    const [fetchDataApi, apiResponse, isLoading, error] = api;
    // Protect API fetcher from accidental re-renders
    const fetchData = useMemo(() => {
        return fetchDataApi;
    }, []);
    const serverSidePagination = useMemo(() => {
        var _a;
        return (_a = apiConfig.serverSidePagination) !== null && _a !== void 0 ? _a : true;
    }, [apiConfig]);
    const serverSideSorting = useMemo(() => {
        var _a;
        return (_a = apiConfig.serverSideSorting) !== null && _a !== void 0 ? _a : true;
    }, [apiConfig]);
    const rowsPerPage = useMemo(() => {
        return apiConfig.rowsPerPage || 20;
    }, [apiConfig]);
    const tableState = useMemo(() => {
        return {
            filters,
            order,
            orderBy,
            page,
            selectedRows,
        };
    }, [filters, order, orderBy, selectedRows, serverSidePagination && page]);
    useEffect(() => {
        onOrderChange === null || onOrderChange === void 0 ? void 0 : onOrderChange(tableState);
    }, [order, orderBy]);
    useEffect(() => {
        onFiltersChange === null || onFiltersChange === void 0 ? void 0 : onFiltersChange(tableState);
    }, [filters]);
    useEffect(() => {
        onPageChange === null || onPageChange === void 0 ? void 0 : onPageChange(tableState);
    }, [page]);
    useEffect(() => {
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(tableState);
    }, [selectedRows]);
    useEffect(() => {
        setSelectedRows === null || setSelectedRows === void 0 ? void 0 : setSelectedRows([]);
    }, [apiResponse]);
    // Rows after mapping, filtering, and sorting
    const rows = useMemo(() => {
        if (!(apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.data))
            return [];
        const transformedData = apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.data.map((r) => rowTransform(r, tableState)).filter(Boolean);
        if (!sortPredicate)
            return transformedData;
        if (!orderBy)
            return transformedData;
        return transformedData.sort((a, b) => sortPredicate(a, b, tableState));
    }, [apiResponse, rowTransform, sortPredicate, tableState, rowsPerPage]);
    // Represents the rows that will be displayed, if serverside pagination is enabled it will display all rows of that page.
    // If serverside pagination is disabled, it will slice the rows based on the page and rowsPerPage
    const cappedRows = useMemo(() => {
        if (serverSidePagination)
            return rows;
        return rows.slice((page - 1) * rowsPerPage, page * rowsPerPage);
    }, [rows, page, rowsPerPage, serverSidePagination]);
    // Represents the total number of rows to be displayed in the page.
    // It can be affected by rowsCap or in case of server side pagination, the total count from the API response
    const totalCount = useMemo(() => {
        var _a;
        if (!serverSidePagination)
            return (rows === null || rows === void 0 ? void 0 : rows.length) || 0;
        return (_a = apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.totalCount) !== null && _a !== void 0 ? _a : 0;
    }, [apiResponse, rowTransform, tableState]);
    useEffect(() => {
        if (serverSidePagination)
            return;
        if ((rows === null || rows === void 0 ? void 0 : rows.length) === (apiResponse === null || apiResponse === void 0 ? void 0 : apiResponse.totalCount))
            return;
        if (!rowTransform)
            return;
        setPage(1);
    }, [apiResponse, rowTransform, tableState]);
    // Initial render, only fetch data when server side is disabled.
    useEffect(() => {
        if (serverSidePagination || serverSideSorting)
            return;
        const params = (typeof apiConfig.params === "function" ? apiConfig.params(tableState) : apiConfig.params) || {};
        if (serverSidePagination) {
            params[apiConfig.limitKey || "limit"] = rowsPerPage;
            params[apiConfig.offsetKey || "offset"] = rowsPerPage * (page - 1);
            params[apiConfig.orderByKey || "orderBy"] = orderBy;
            params[apiConfig.orderKey || "order"] = order;
        }
        if (fetchData)
            fetchData(params);
    }, [refetch, fetchData, apiConfig.params, serverSideSorting && tableState]);
    // Refetch data on page change, only when server side pagination is enabled
    useEffect(() => {
        if (!serverSidePagination && !serverSideSorting)
            return;
        const params = (typeof apiConfig.params === "function" ? apiConfig.params(tableState) : apiConfig.params) || {};
        params[apiConfig.limitKey || "limit"] = rowsPerPage;
        params[apiConfig.offsetKey || "offset"] = rowsPerPage * (page - 1);
        params[apiConfig.orderByKey || "orderBy"] = orderBy;
        params[apiConfig.orderKey || "order"] = order;
        if (fetchData)
            fetchData(params);
    }, [
        refetch,
        apiConfig.params,
        tableState.filters,
        tableState.order,
        tableState.orderBy,
        tableState.page,
        rowsPerPage,
    ]);
    const handleFilterChange = (column) => (_filters) => {
        setFilters((prev) => (Object.assign(Object.assign({}, prev), { [column.id]: _filters })));
        setPage(1);
    };
    const handleSelectRow = (row, checked) => {
        if (checked) {
            setSelectedRows([row, ...selectedRows]);
            return;
        }
        setSelectedRows(selectedRows.filter((r) => r._id !== row._id));
    };
    const handleSelectAllClick = (event) => {
        if (selectedRows.length > 0) {
            setSelectedRows([]);
            return;
        }
        if (event.target.checked) {
            setSelectedRows(rows);
            return;
        }
        setSelectedRows([]);
    };
    // Columns with all labels compiled to string.
    const columns = useMemo(() => {
        return _columns.map((column) => {
            var _a;
            return (Object.assign(Object.assign({}, column), { label: typeof column.label === "string" ? column.label : intl.formatMessage(column.label), filterOptions: (_a = column.filterOptions) === null || _a === void 0 ? void 0 : _a.map((filter) => (Object.assign(Object.assign({}, filter), { label: typeof filter.label === "string" ? filter.label : intl.formatMessage(filter.label) }))) }));
        });
    }, [_columns]);
    const handleOrderChange = (columnId) => {
        setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
        setOrderBy(columnId);
    };
    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };
    const selectableRows = useMemo(() => {
        if (!canSelectRow)
            return rows;
        return rows.filter((r) => canSelectRow(r));
    }, [rows, canSelectRow]);
    const numSelectedSelectable = useMemo(() => {
        if (!canSelectRow)
            return selectedRows.length;
        return selectedRows.filter((r) => canSelectRow(r)).length;
    }, [selectedRows, canSelectRow]);
    return (React.createElement(PeekTableContainer, null,
        React.createElement(PeekTable, Object.assign({}, tableProps, { stickyHeader: true }),
            onSelectionChange && selectableRows.length > 0 && (React.createElement(SelectableTableHead, { rowCount: selectableRows.length, numSelected: numSelectedSelectable, onSelectAllClick: (event) => {
                    if (selectedRows.length > 0) {
                        setSelectedRows([]);
                        return;
                    }
                    if (event.target.checked) {
                        // @ts-ignore rows are of type T
                        setSelectedRows(selectableRows);
                        return;
                    }
                    setSelectedRows([]);
                } })),
            columns.map((column) => (React.createElement(PeekTableCell, Object.assign({}, column.columnCellProps, { key: column.id }),
                React.createElement(PeekStack, { direction: "row", spacing: 0.5, alignItems: "center" },
                    column.sortable ? (React.createElement(PeekTableSortLabel, { sx: { fontWeight: "fontWeightMedium" }, active: orderBy === column.id, direction: orderBy === column.id ? order : "asc", onClick: () => handleOrderChange(column.id), IconComponent: () => (React.createElement(ArrowDrop, { active: orderBy === column.id, direction: orderBy === column.id ? order : "desc" })) },
                        React.createElement(PeekTypography, { fontWeight: "fontWeightMedium" }, column.label))) : (React.createElement(PeekStack, { direction: "row", spacing: 0.6 },
                        React.createElement(PeekBox, null,
                            React.createElement(PeekTypography, { fontWeight: "fontWeightMedium", whiteSpace: "nowrap" }, column.label)),
                        column.infoText && (React.createElement(PeekTooltip, { title: React.createElement(PeekTypography, null, column.infoText), placement: "top" },
                            React.createElement(PeekTypography, { variant: "h3" },
                                React.createElement(Info, null)))))),
                    column.filterOptions && (React.createElement(PeekTableHeadFilter, { onFilterChange: handleFilterChange(column), filters: column.filterOptions })))))),
            React.createElement(PeekTableBody, null, !!(cappedRows === null || cappedRows === void 0 ? void 0 : cappedRows.length) &&
                cappedRows.map((row) => {
                    return (React.createElement(TableRow, { key: row._id, row: row, columns: columns, onClick: onClick, isSelected: selectedRows.some((r) => r._id === row._id), showSelectionColumn: !!onSelectionChange && selectableRows.length > 0, onSelect: onSelectionChange && (!canSelectRow || canSelectRow(row))
                            ? (checked) => handleSelectRow(row, checked)
                            : undefined, actionsCover: actionsCover, actions: actions, selectedContent: rowSelectedContent, expandededContent: rowExpandedContent, expandButton: expandButton }));
                }))),
        error && React.createElement(NoDataFound, { message: error.message, error: true }),
        isLoading && React.createElement(PeekTableSkeletonLoader, { numberOfLines: 5 }),
        !isLoading && !error && !(rows === null || rows === void 0 ? void 0 : rows.length) && (React.createElement(NoDataFound, { message: typeof notFoundMessage === "string" ? notFoundMessage : intl.formatMessage(notFoundMessage) })),
        React.createElement(TablePaginationContainer, { totalCount: totalCount, onPageChange: handleChangePage, page: page, rowsPerPage: rowsPerPage })));
}
